import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { FiMenu, FiX, FiSun, FiMoon } from 'react-icons/fi';

const Nav = styled(motion.nav)`
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 100;
  padding: 1rem 2rem;
  background: ${({ theme, scrolled }) => 
    scrolled ? theme.navbar : 'transparent'};
  backdrop-filter: ${({ scrolled }) => 
    scrolled ? 'blur(10px)' : 'none'};
  box-shadow: ${({ scrolled, theme }) => 
    scrolled ? theme.shadowSm : 'none'};
  transition: all 0.3s ease;
  font-family: 'Inter', sans-serif;
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(motion.h1)`
  font-size: 1.55rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -2.5px;
  background: ${({ theme }) => theme.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  cursor: pointer;
`;

const NavLinks = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(motion.a)`
  color: ${({ theme }) => theme.text};
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.4rem 0.2rem;
  border-radius: 4px;
  position: relative;
  transition: all 0.3s ease;
  text-transform: uppercase;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.gradient};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  &.active {
    color: ${({ theme }) => theme.primary};

    &::after {
      width: 100%;
    }
  }
`;

const ThemeToggle = styled(motion.button)`
  background: ${({ theme }) => theme.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  padding: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  border-radius: 50%;
  box-shadow: ${({ theme }) => theme.shadowMd};
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 100%;
    background: ${({ theme }) => theme.gradient};
    transition: width 0.3s ease;
    opacity: 0.2;
    z-index: 0;
  }

  &:hover {
    color: ${({ theme }) => theme.primary};

    &::after {
      width: 100%;
    }
  }

  svg {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    position: absolute;
    top: 1.5rem;
    right: 5rem;
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: ${({ theme }) => theme.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  padding: 0.6rem;
  font-size: 1.5rem;
  border-radius: 50%;
  box-shadow: ${({ theme }) => theme.shadowMd};

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: ${({ theme }) => theme.backgroundAlt};
  z-index: 999;
  display: flex;
  flex-direction: column;
  padding: 2rem 1.5rem;
  gap: 1rem;

  background: linear-gradient(
    to bottom,
    ${({ theme }) => theme.backgroundAlt},
    ${({ theme }) => `${theme.backgroundAlt}f0`}
  );
`;

const MobileNavLink = styled(motion.a)`
  color: ${({ theme }) => theme.text};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => `${theme.primary}20`};
  transition: all 0.3s ease;
  opacity: 0.8;

  &:hover {
    opacity: 1;
    padding-left: 0.5rem;
    color: ${({ theme }) => theme.primary};
  }
`;

const CloseButton = styled(MobileMenuButton)`
  position: absolute;
  top: 1.5rem;
  right: 2rem;
  display: flex;
`;

const navVariants = {
  hidden: { y: -100 },
  visible: { 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20
    }
  }
};

const linkVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: i => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5
    }
  })
};

const mobileMenuVariants = {
  closed: {
    opacity: 0,
    x: "100%",
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 40
    }
  },
  open: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 40
    }
  }
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const [activeLink, setActiveLink] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
  }, [isMobileMenuOpen]);

  const navLinks = [
    { href: '#home', text: 'Home' },
    { href: '#about', text: 'About' },
    { href: '#services', text: 'Services' },
    { href: '#resume', text: 'Resume' },
    { href: '#portfolio', text: 'Portfolio' },
    { href: '#contact', text: 'Contact' }
  ];

  const handleScrollToSection = (event, sectionId) => {
    event.preventDefault();
    console.log('Attempting to scroll to:', sectionId); // Log section ID
    const section = document.querySelector(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.error(`Section with ID ${sectionId} not found`);
    }
  };

  return (
    <Nav
      variants={navVariants}
      initial="hidden"
      animate="visible"
      scrolled={isScrolled}
    >
      <NavContainer>
        <Logo
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          KIYAEH.
        </Logo>
        
        <NavLinks>
          {navLinks.map((link, i) => (
            <NavLink
              key={link.href}
              href={link.href} // This can remain for styling, but we'll handle click events
              className={activeLink === link.href.slice(1) ? 'active' : ''}
              onClick={(event) => {
                setActiveLink(link.href.slice(1));
                handleScrollToSection(event, link.href);
              }}
              custom={i}
              variants={linkVariants}
              initial="hidden"
              animate="visible"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {link.text}
            </NavLink>
          ))}
          <ThemeToggle
            onClick={toggleTheme}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {theme === 'light' ? <FiMoon /> : <FiSun />}
          </ThemeToggle>
        </NavLinks>

        <MobileMenuButton
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FiMenu />
        </MobileMenuButton>
      </NavContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            variants={mobileMenuVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <CloseButton
              onClick={() => setIsMobileMenuOpen(false)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX />
            </CloseButton>
            {navLinks.map((link, i) => (
              <MobileNavLink
                key={link.href}
                href={link.href} // This can remain for styling, but we'll handle click events
                onClick={(event) => {
                  setActiveLink(link.href.slice(1));
                  handleScrollToSection(event, link.href);
                  setIsMobileMenuOpen(false);
                }}
                custom={i}
                variants={linkVariants}
                initial="hidden"
                animate="visible"
              >
                {link.text}
              </MobileNavLink>
            ))}
            <ThemeToggle
              onClick={toggleTheme}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {theme === 'light' ? <FiMoon /> : <FiSun />}
            </ThemeToggle>
          </MobileMenu>
        )}
      </AnimatePresence>
    </Nav>
  );
};

export default Navbar;